import React, { useEffect, useState } from "react";
import { HelmetProvider } from "react-helmet-async";
import { BrowserRouter as Router, Switch, Route } from "react-router-dom";
import { api } from "./consts/api";
import RegionGuard from "./utilities/RegionGuard";
import EULayout from "./regionLayouts/EULayout";
import FRLayout from "./regionLayouts/FRLayout";
import USLayout from "./regionLayouts/USLayout";
import RegionContext from "./RegionContext";

function determineRegion(countryName) {
  const euCountries = [
    "Germany",
    "Spain",
    "Italy",
    "The Netherlands",
    "Belgium",
    "Sweden",
    "Finland",
    "Poland",
    "Austria",
    "Switzerland",
    "Ireland",
    "Portugal",
    "Norway",
    "Denmark",
    "United Kingdom",
    "Luxembourg",
  ];

  if (euCountries.includes(countryName)) return "EU";
  if (countryName === "France") return "FR";
  return "US";
}

function App() {
  // 1) read the override query-param (if any) on *every* render
  const params = new URLSearchParams(window.location.search);
  const overrideParam = params.get("region")?.toUpperCase();

  // 2) read any previously saved region from sessionStorage
  const savedRegion = sessionStorage.getItem("region");

  // 3) seed React state: query-param wins, then savedRegion, else null
  const [region, setRegion] = useState(overrideParam || savedRegion || null);

  useEffect(() => {
    // a) if we got a fresh overrideParam, save it to sessionStorage & skip lookup
    if (overrideParam) {
      sessionStorage.setItem("region", overrideParam);
      return;
    }

    // b) if we already had something in storage, skip lookup
    if (savedRegion) {
      return;
    }

    // c) only when neither query-param nor storage gave us a region do the geo lookup
    fetch(api + `/Geolocation/GetUserCountry`)
      .then((res) => res.json())
      .then((country) => {
        const countryName = country?.countryName;
        const newRegion = countryName ? determineRegion(countryName) : "US";
        setRegion(newRegion);
        sessionStorage.setItem("region", newRegion);
      })
      .catch((err) => {
        console.error("Geolocation lookup failed:", err);
        setRegion("US");
        sessionStorage.setItem("region", "US");
      });
  }, [overrideParam, savedRegion]);

  if (!region) return null; // still determining

  return (
    <HelmetProvider>
      <RegionContext.Provider value={region}>
        <Router>
          <RegionGuard region={region} />
          <Switch>
            {region === "EU" && (
              <Route path="*">
                <EULayout />
              </Route>
            )}
            {region === "FR" && (
              <Route path="*">
                <FRLayout />
              </Route>
            )}
            {region === "US" && (
              <Route path="*">
                <USLayout />
              </Route>
            )}
          </Switch>
        </Router>
      </RegionContext.Provider>
    </HelmetProvider>
  );
}

export default App;
